import React, { useState } from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';

import { languagesHelper as language } from '../../../../util/languages';
import { ListingsCarousel, IconLandscapes } from '../../../../components';
import css from './SectionArticle.module.css';

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show article content.
 * The article content is mainly supposed to be inside a block.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'article'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionArticle = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
    listingsByFilters,
    onCountListingsByFilters,
    pageId
  } = props;

  let currentLocale = language.get();
  const sectionLocale = sectionId?.indexOf('_locale_') !== -1 && sectionId?.slice(sectionId.length - 2)

  if (!!sectionLocale && sectionLocale !== currentLocale) return null;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;
  const isExperienceRewilding = sectionId === "experience-rewilding";
  const isExploreFeaturedListings = sectionId === "explore-featured-listings";
  const isMarketplaceIntroduction = sectionId === "marketplace-introduction";
  const isLandscapeHero = sectionId === "landscape-hero";
  const isWildCoaNetwork = sectionId === "wild-coa-network";

  const heroAsideTexts = isMarketplaceIntroduction && blocks?.filter(b => b.blockId === "hero-left-text" || b.blockId === "hero-right-text");
  const filteredBlocks = isMarketplaceIntroduction ? blocks?.filter(b => b.blockId !== "hero-left-text" && b.blockId !== "hero-right-text") : blocks;

  const showCallToAction = !isExperienceRewilding && !isExploreFeaturedListings && !isWildCoaNetwork;

  const [carouselListings, setCarouselListings] = useState([]);
  const [experienceRewildingBg, setExperienceRewildingBg] = useState(null);

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(className, !!carouselListings?.length && css.withCarouselListings)}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
      pageId={pageId}
      experienceRewildingBg={experienceRewildingBg}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>

          {isLandscapeHero && pageId && (
            <div className={css.landscape}>
              <IconLandscapes type={pageId?.replace("landscape-", "")} />
            </div>
          )}

          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          {showCallToAction && (
            <Field
              data={callToAction}
              className={defaultClasses.ctaButton}
              options={fieldOptions}
              listingsByFilters={listingsByFilters}
              onCountListingsByFilters={onCountListingsByFilters}
              id={sectionId}
              heroAsideTexts={heroAsideTexts}
            />
          )}
        </header>
      ) : null}
      {hasBlocks ? (
        <div
          className={classNames(defaultClasses.blockContainer, css.articleMain, {
            [css.noSidePaddings]: isInsideContainer,
          })}
        >
          <BlockBuilder
            blocks={filteredBlocks}
            sectionId={sectionId}
            ctaButtonClass={defaultClasses.ctaButton}
            options={options}
          />
        </div>
      ) : null}
      {isExperienceRewilding && (
        <div className={css.homepageListings}>
          <ListingsCarousel
            type="homepageListings"
            callToAction={callToAction}
            setCarouselListings={setCarouselListings}
            setExperienceRewildingBg={setExperienceRewildingBg}
          />
        </div>
      )}
      {isExploreFeaturedListings && (
        <div className={css.homepageListings}>
          <ListingsCarousel
            type="landscapeListings"
            pageId={pageId}
            callToAction={callToAction}
            theme="dark"
            setCarouselListings={setCarouselListings}
          />
        </div>
      )}
      {isWildCoaNetwork && (
        <div className={css.homepageListings}>
          <ListingsCarousel
            type="landscapeListings"
            pageId={"landscape-greater-coa-valley"}
            callToAction={callToAction}
            theme="dark"
            setCarouselListings={setCarouselListings}
          />
        </div>
      )}
    </SectionContainer>
  );
};

export default SectionArticle;
